import { Flex, Link, LoadingIcon, Stack } from "basis";
import { getHref } from "library/api";
import { useProducts } from "library/hooks/useProducts";
import { useEffect, useState } from "react";
import { ErrorPanel } from "../../ErrorPanel/ErrorPanel";
import { InfoBlock } from "../../InfoBlock/InfoBlock";
import { PlanCard } from "../../PlanCard/PlanCard";

type Plan = {
  balance: number;
  days_left: number;
  expiry_date: string;
  links: [];
  original_balance: number;
  outstanding_balance: number;
  posted_date: string;
  purchase_amount: number;
  reference_number: string;
  start_date: string;
  status: string;
  store: string;
  term: number;
  transaction_date: string;
  type: string;
};

type Props = {
  plansHref: string;
  productId: string;
};

export const Plans = ({ plansHref, productId }: Props) => {
  // TODO: Convert to react-query
  const [state, setState] = useState<{
    plans: Plan[];
    isLoading: boolean;
    isError: boolean;
  }>({
    plans: [],
    isLoading: true,
    isError: false,
  });
  const { plans, isLoading, isError } = state;
  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];
  const product = products.find((p) => p.id === productId);
  const activePlans = plans
    .filter((plan) => plan.balance > 0)
    .sort(
      (plan1, plan2) =>
        new Date(plan1.expiry_date).getTime() -
        new Date(plan2.expiry_date).getTime(),
    );

  useEffect(() => {
    if (!plansHref) return;

    (async () => {
      try {
        const { data } = await getHref<Plan[]>(plansHref);

        setState({
          plans: data,
          isLoading: false,
          isError: false,
        });
      } catch (e) {
        setState({
          plans: [],
          isLoading: false,
          isError: true,
        });
      }
    })();
  }, [plansHref]);

  if (isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  if (isError) {
    return (
      <ErrorPanel icon="error-documents">
        We were unable to retrieve your interest-free plans as there has been an
        issue on our end. Please check back again soon or{" "}
        <Link href="/contact" newTab={false}>
          contact support
        </Link>{" "}
        for help.
      </ErrorPanel>
    );
  }

  if (activePlans.length === 0) {
    return (
      <InfoBlock iconName="no-interest-free" trackId="no-interest-free">
        You currently have no active interest free plans on your{" "}
        {product?.name ?? ""} account.
      </InfoBlock>
    );
  }

  return (
    <Stack gap="10">
      {activePlans.map((plan) => (
        <PlanCard
          loanAmount={plan.purchase_amount}
          loanBalance={plan.balance}
          currentLoanTerm={plan.term}
          currentMaturityDate={plan.expiry_date}
          openedOnDate={plan.posted_date}
          store={plan.store}
          key={plan.reference_number}
        />
      ))}
    </Stack>
  );
};
